import React from 'react';
import { Box as MuiBox, BoxProps as MuiBoxProps } from '@mui/material';
import { PounceStyleProps, extractPounceProps, mapPouncePropsToSx } from '../../utils';
import withAutoHeadingLevel from '../../utils/withAutoHeadingLevel';

export interface ArticleProps extends Omit<MuiBoxProps, keyof PounceStyleProps>, PounceStyleProps {
  /** Additional styles to apply (combined with pounce props) */
  sx?: MuiBoxProps['sx'];
}

/**
 * Article renders an `<article>` tag and should be used whenever you need this specific semantic.
 *
 * The core benefit of this component is the fact that it automatically increases the heading level of the
 * [Heading](../?path=/docs/pouncev2-general-text-heading--docs) components inside it by 1. See the examples below to better understand
 * this.
 *
 * Provides full pounce API compatibility while built on MUI infrastructure.
 *
 * **NOTE**: this component is functionally the same as [Section](../?path=/docs/components-section), but semantically
 * it renders an `<article />` tag instead of a `<section />` tag.
 */
export const ArticleBase = React.forwardRef<HTMLElement, ArticleProps>(function Article(
  { children, sx, ...rest },
  ref,
) {
  const { pounceProps, otherProps } = extractPounceProps(rest);
  const pounceStyles = mapPouncePropsToSx(pounceProps);

  // Combine pounce-generated styles with user-provided sx
  const combinedSx = { ...pounceStyles, ...sx };

  return (
    <MuiBox ref={ref} component="article" sx={combinedSx} {...otherProps}>
      {children}
    </MuiBox>
  );
});

export const Article = withAutoHeadingLevel()(ArticleBase) as React.FC<ArticleProps>;
